<script lang="ts" setup>
import { HgRadioButton } from '@smg-real-estate/domus';
import { ref, watch } from 'vue';

import { FormHoverState } from '@/components/common';
import { isPlatformHg, isPlatformIs24 } from '@/helpers/Platform/Platform';

interface Props {
  text: string;
  value?: string | null;
  checked?: string;
}

const props = withDefaults(defineProps<Props>(), {
  value: null,
  checked: '',
});

const emit = defineEmits<{
  onClick: [value: string];
}>();

// Reactive data
const isChecked = ref(props.checked);

// Computed platform detection
const isHg = isPlatformHg();
const isIs24 = isPlatformIs24();

// Watch for changes in checked prop
watch(
  () => props.checked,
  (value) => {
    isChecked.value = value;
  },
);

// Methods
const handleClick = () => {
  if (props.value) {
    emit('onClick', props.value);
  }
};
</script>

<template>
  <FormHoverState :selected="isChecked === props.value">
    <div
      ref="RadioInput"
      data-cy="PropertyRadioBox"
      :class="{
        [$style.wrapperHg]: isHg,
        [$style.wrapperIs24]: isIs24,
        [$style.wrapperIs24Checked]: isIs24 && isChecked === props.value,
        [$style.wrapperHgChecked]: isHg && isChecked === props.value,
      }"
      @click="handleClick"
    >
      <div>
        <img
          v-if="parseInt(props.value || '0') === 0 && isHg"
          src="../../../../../assets/icons/apartment-hg.svg?assetResource"
          alt="..."
          loading="lazy"
        />
        <img
          v-if="parseInt(props.value || '0') === 1 && isHg"
          src="../../../../../assets/icons/house-hg.svg?assetResource"
          alt="..."
          loading="lazy"
        />

        <img
          v-if="parseInt(props.value || '0') === 0 && isIs24"
          src="../../../../../assets/icons/apartment-is24.svg?assetResource"
          alt="..."
          loading="lazy"
        />
        <img
          v-if="parseInt(props.value || '0') === 1 && isIs24"
          src="../../../../../assets/icons/house-is24.svg?assetResource"
          alt="..."
          loading="lazy"
        />

        <div data-cy="PropertyRadioBox-Name" :class="$style.text">{{ props.text }}</div>

        <div :class="[$style.radio]">
          <HgRadioButton
            v-model="isChecked"
            name="option1"
            :inputValue="props.value as string"
            label=""
          />
        </div>
      </div>
    </div>
  </FormHoverState>
</template>

<style lang="scss" module>
@use '@/assets/variables' as *;

@mixin wrapper {
  position: relative;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 175px;
  border: 1px solid $color-grey-60;
  border-radius: $border-radius;
  stroke: $color-grey-70;

  > div {
    min-width: 80px;
    text-align: center;
  }
}

@mixin base-effect-hg {
  background-color: #e8effd;
  @include common-box-shadow(rgba(44, 79, 167, 0.1));
  @include common-border(#2c4fa7);
}

@mixin base-effect-is24 {
  background-color: #e9fff1;
  @include common-box-shadow(rgba(0, 219, 199, 0.1));
  @include common-border($color-teal-110);
}

.wrapperHg {
  @include wrapper;

  &:hover {
    @include base-effect-hg;
  }
}

.wrapperIs24 {
  @include wrapper;

  &:hover {
    @include base-effect-is24;
  }
}

.wrapperIs24Checked {
  border: 2px solid $color-teal-100 !important;
}

.wrapperHgChecked {
  border-width: 2px !important;
}

.radio {
  position: absolute;
  top: 10px;
  right: 10px;

  label {
    display: block;
  }
}

.text {
  color: #333333;
  margin-top: $space-50;

  @include body-2-bold;
}
</style>
